// PdfPageTitle.jsx
import { View, StyleSheet } from "@react-pdf/renderer";
import { useMemo } from "react";
import PdfText from "./PdfText";

const COLORS = {
  primary: "#1a3a5c",
  accent: "#2c6fbb",
  border: "#d0d7de",
  primaryLight: "#e8eef4",
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 14,
  },

  // ── Divider variants ──
  dividerNone: {},
  dividerLine: {
    borderBottom: `1px solid ${COLORS.border}`,
    paddingBottom: 8,
  },
  dividerThick: {
    borderBottom: `2.5px solid ${COLORS.primary}`,
    paddingBottom: 8,
  },
  dividerAccent: {
    borderBottom: `2.5px solid ${COLORS.accent}`,
    paddingBottom: 8,
  },

  // ── Layout ──
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  titleGroup: {
    flex: 1,
  },
  badge: {
    backgroundColor: COLORS.primaryLight,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
  },
});

const DIVIDER_MAP = {
  none: styles.dividerNone,
  line: styles.dividerLine,
  thick: styles.dividerThick,
  accent: styles.dividerAccent,
};

/**
 * Page-level title — the most prominent element on a PDF page.
 *
 * @example
 * <PdfPageTitle>Loan Application</PdfPageTitle>
 *
 * <PdfPageTitle subtitle="Submitted March 31, 2026">
 *   Financing Request
 * </PdfPageTitle>
 *
 * <PdfPageTitle
 *   subtitle="Complete borrower and property details"
 *   badge="Draft"
 *   divider="thick"
 * >
 *   Loan Application
 * </PdfPageTitle>
 *
 * <PdfPageTitle
 *   divider="accent"
 *   rightContent={<PdfBuilderLogo size={28} />}
 * >
 *   LendMarq — Loan Application
 * </PdfPageTitle>
 */
const PdfPageTitle = ({
  children,
  subtitle,
  description,
  badge,
  divider = "line",
  // divider = "thick",
  rightContent,
  mb,
  style = {},
}) => {
  const containerStyle = useMemo(() => {
    const s = [styles.container, DIVIDER_MAP[divider] || styles.dividerThick];

    if (mb !== undefined) s.push({ marginBottom: mb });
    if (style && Object.keys(style).length > 0) s.push(style);

    return s;
  }, [divider, mb, style]);

  return (
    <View style={containerStyle}>
      <View style={styles.headerRow}>
        <View style={styles.titleGroup}>
          {/* Badge */}
          {badge && (
            <View
              style={[
                styles.badge,
                { marginBottom: 4, alignSelf: "flex-start" },
              ]}
            >
              <PdfText size="xs" color="primary" bold uppercase>
                {badge}
              </PdfText>
            </View>
          )}

          {/* Main title */}
          <PdfText
            size="3xl"
            color="primary"
            bold
            mb={subtitle || description ? 3 : 0}
          >
            {children}
          </PdfText>

          {/* Subtitle */}
          {subtitle && (
            <PdfText size="md" color="secondary" mb={description ? 2 : 0}>
              {subtitle}
            </PdfText>
          )}

          {/* Description */}
          {description && (
            <PdfText size="sm" color="muted" lineHeight={1.5}>
              {description}
            </PdfText>
          )}
        </View>

        {/* Right slot (logo, page number, date, etc.) */}
        {rightContent && <View style={{ marginLeft: 12 }}>{rightContent}</View>}
      </View>
    </View>
  );
};

export default PdfPageTitle;
